import React, { useState, useCallback } from 'react';
import { playbookApi } from '../../api/playbookApi';
import type { PlaybookTransitionGuard, PlaybookStatusFull } from '../../types/playbook.types';
import { GuardConfigForm } from './config-forms/GuardConfigForms';

interface GuardListPanelProps {
  transitionId: string;
  guards: PlaybookTransitionGuard[];
  statuses: PlaybookStatusFull[];
  onRefresh: () => void;
}

const GUARD_TYPES = [
  'role_required', 'assignee_only', 'status_is', 'field_has_value',
  'expression', 'document_attached', 'dependency_complete', 'playbook_status', 'approval_received',
];

const GuardListPanel: React.FC<GuardListPanelProps> = ({ transitionId, guards, statuses, onRefresh }) => {
  const [addingType, setAddingType] = useState<string | null>(null);
  const [newConfig, setNewConfig] = useState<Record<string, unknown>>({});
  const [newErrorMsg, setNewErrorMsg] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<Record<string, unknown>>({});
  const [editErrorMsg, setEditErrorMsg] = useState('');

  const handleAdd = useCallback(async () => {
    if (!addingType) return;
    try {
      await playbookApi.addGuard(transitionId, {
        guard_type: addingType,
        configuration: newConfig,
        error_message: newErrorMsg || undefined,
        sort_order: guards.length,
      });
      setAddingType(null);
      setNewConfig({});
      setNewErrorMsg('');
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, addingType, newConfig, newErrorMsg, guards.length, onRefresh]);

  const handleUpdate = useCallback(async (guardId: string) => {
    try {
      await playbookApi.updateGuard(transitionId, guardId, {
        configuration: editConfig,
        error_message: editErrorMsg || undefined,
      });
      setEditingId(null);
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, editConfig, editErrorMsg, onRefresh]);

  const handleRemove = useCallback(async (guardId: string) => {
    try {
      await playbookApi.removeGuard(transitionId, guardId);
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, onRefresh]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h5 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Guards ({guards.length})</h5>
        {!addingType && (
          <select
            className="rounded border border-gray-300 px-1 py-0.5 text-[10px] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
            value=""
            onChange={(e) => { setAddingType(e.target.value); setNewConfig({}); }}
          >
            <option value="">+ Add guard</option>
            {GUARD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>

      {/* Existing guards */}
      {guards.map((g) => (
        <div key={g.id} className="rounded border border-gray-200 dark:border-gray-700 p-2" style={{ animation: 'fadeSlideIn 200ms ease-out' }}>
          <div className="flex items-center justify-between mb-1">
            <span className="rounded-full bg-lex-accent-soft px-2 py-0.5 text-[9px] font-medium text-lex-accent">
              {g.guard_type}
            </span>
            <div className="flex gap-1">
              <button type="button" onClick={() => { setEditingId(g.id === editingId ? null : g.id); setEditConfig(g.configuration); setEditErrorMsg(g.error_message ?? ''); }} className="text-[9px] text-lex-accent hover:text-lex-accent-hover">
                {editingId === g.id ? 'Close' : 'Edit'}
              </button>
              <button type="button" onClick={() => handleRemove(g.id)} className="text-[9px] text-rose-500 hover:text-rose-700">Remove</button>
            </div>
          </div>
          {g.error_message && <p className="text-[9px] text-gray-400 mb-1">Error: {g.error_message}</p>}
          {editingId === g.id && (
            <div className="mt-2 space-y-2 border-t border-gray-100 dark:border-gray-700 pt-2">
              <GuardConfigForm guardType={g.guard_type} config={editConfig} onChange={setEditConfig} statuses={statuses} />
              <div>
                <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">Error Message</label>
                <input type="text" className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300" value={editErrorMsg} onChange={(e) => setEditErrorMsg(e.target.value)} />
              </div>
              <button type="button" onClick={() => handleUpdate(g.id)} className="rounded bg-lex-accent px-3 py-1 text-[10px] font-medium text-lex-accent-contrast hover:bg-lex-accent-hover">Save</button>
            </div>
          )}
        </div>
      ))}

      {/* Add new guard form */}
      {addingType && (
        <div className="rounded border border-lex-accent p-2 bg-lex-accent-soft" style={{ animation: 'coverageDrillDown 200ms ease-out' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-lex-accent">New: {addingType}</span>
            <button type="button" onClick={() => setAddingType(null)} className="text-[9px] text-gray-400 hover:text-gray-600">Cancel</button>
          </div>
          <GuardConfigForm guardType={addingType} config={newConfig} onChange={setNewConfig} statuses={statuses} />
          <div className="mt-2">
            <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5">Error Message</label>
            <input type="text" className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300" value={newErrorMsg} onChange={(e) => setNewErrorMsg(e.target.value)} placeholder="Optional error message" />
          </div>
          <button type="button" onClick={handleAdd} className="mt-2 rounded bg-lex-accent px-3 py-1 text-[10px] font-medium text-lex-accent-contrast hover:bg-lex-accent-hover">Add Guard</button>
        </div>
      )}
    </div>
  );
};

export default GuardListPanel;
